import type { ECharts } from 'echarts/core'

// Keep an ECharts instance sized to its host element using a ResizeObserver.
//
// Unlike EchartsChartManager this adds NO `window` resize listener: a fluid
// (width:100%) container's content box already changes on viewport resize, so
// the observer covers that case, and skipping the global listener keeps this
// safe in lifecycle-less `onDomLoad` behaviours where there is no teardown hook
// to remove it (a leaked listener would pin the chart and its detached DOM).
//
// Returns a teardown that disconnects the observer and cancels any pending
// frame. Callers with a disposal hook should invoke it; callers without one can
// rely on the observer becoming collectable once the host element is dropped.
export function observeEchartsResize(
  chart: ECharts,
  element: HTMLElement
): () => void {
  let frame: number | null = null

  // Defer the resize to the next frame so the ResizeObserver callback returns
  // before ECharts mutates the DOM. Otherwise the observer can re-fire on the
  // same observation cycle, surfacing a "ResizeObserver loop completed with
  // undelivered notifications" warning.
  const handleResize = (): void => {
    if (frame !== null) return
    frame = window.requestAnimationFrame(() => {
      frame = null
      chart.resize()
    })
  }

  const observer = new ResizeObserver(handleResize)
  observer.observe(element)

  return () => {
    observer.disconnect()
    if (frame !== null) {
      window.cancelAnimationFrame(frame)
      frame = null
    }
  }
}
